import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  //   faPlus,
  faCheck,
  faCircleInfo,
  faExclamationTriangle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons"
import { useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import * as yup from "yup"
import { Formik, Form, Field, ErrorMessage } from "formik"
import {
  useEducationalVideo,
  useLogEducationalVideoEvent,
} from "../../hooks/useEducationalVideo"
import { useSubmitCustomerFeedback } from "../../hooks/useCustomerFeedback"
import { getTrackingIds } from "../../utils/visitor"
import { scanEventTypes, scanSources } from "../../constants"
import VideoPlayer from "./VideoPlayer"

const feedbackSchema = yup.object().shape({
  comments: yup
    .string()
    .max(500, "Comments must be 500 characters or less")
    .optional(),
  illustrations_helpful: yup
    .mixed()
    .nullable()
    .oneOf([true, false, null], "Please select a valid option")
    .test(
      "is-answered",
      "Please select an option",
      (value) => value !== undefined
    ),
  recommend_report: yup
    .mixed()
    .oneOf([true, false], "Please select a valid option")
    .required("Please select an option"),
  report_delivery: yup
    .string()
    .max(50, "Must be 50 characters or less")
    .required("Please select how you received your report"),
  spe_helpful: yup
    .mixed()
    .nullable()
    .oneOf([true, false, null], "Please select a valid option")
    .test(
      "is-answered",
      "Please select an option",
      (value) => value !== undefined
    ),
})

const EducationalVideos = () => {
  const { uuid } = useParams()
  const [searchParams] = useSearchParams()
  const {
    data: educationalVideoData,
    isLoading: isEducationalVideoDataLoading,
    error,
  } = useEducationalVideo(uuid)

  const { mutateAsync: submitFeedback, isPending: isFeedbackSubmitting } =
    useSubmitCustomerFeedback()

  const { mutate: logEvent } = useLogEducationalVideoEvent()

  //   const [expandedItems, setExpandedItems] = useState(new Set())
  const [isSubmitted, setIsSubmitted] = useState(false)

  /*
    Opens from the admin Video Logs preview button carry ?source=preview.

    Nothing is tracked for a preview — the page renders exactly as a patient
    sees it, but no event reaches the API. `source` is still sent on the events
    that do go out, so if this guard is ever lost the API can still recognise a
    preview and keep it out of the counts.
  */
  const isPreview = searchParams.get("source") === scanSources.PREVIEW
  const source = isPreview ? scanSources.PREVIEW : scanSources.QR

  // Read once per mount: the ids outlive this page, so re-reading storage on
  // every render would only churn.
  const trackingIds = useMemo(() => getTrackingIds(), [])

  const pageOpenLoggedRef = useRef(false)
  const feedbackStartedLoggedRef = useRef(false)

  // The scan itself. Logged once the report resolves, so a mistyped or expired
  // UUID is not counted as a scan of a real report.
  useEffect(() => {
    if (isPreview || !uuid || !educationalVideoData) return
    if (pageOpenLoggedRef.current) return

    pageOpenLoggedRef.current = true
    logEvent({
      uuid,
      eventType: scanEventTypes.PAGE_OPEN,
      source,
      ...trackingIds,
    })
  }, [isPreview, uuid, educationalVideoData, source, trackingIds, logEvent])

  // First touch of any field counts as starting the form. Fired once per visit;
  // the matching submit event is recorded by the API when the response is
  // stored, so an abandoned form is visible as started-but-never-submitted.
  const handleFeedbackStarted = () => {
    if (isPreview || !uuid || feedbackStartedLoggedRef.current) return

    feedbackStartedLoggedRef.current = true
    logEvent({
      uuid,
      eventType: scanEventTypes.FEEDBACK_STARTED,
      source,
      ...trackingIds,
    })
  }

  /*
    Watched at the form level so every control is covered without threading a
    callback through each one.

    Both click and focus are needed: iOS Safari does not reliably focus a button
    on tap, and that is the platform most of these QR codes are scanned on.
    `closest` keeps a stray tap on the card's padding from counting as engagement.
  */
  const handleFormInteraction = (event) => {
    if (!event.target.closest("button, textarea, input, select")) return
    handleFeedbackStarted()
  }

  //   const toggleExpanded = (index) => {
  //     const newExpandedItems = new Set(expandedItems)
  //     if (newExpandedItems.has(index)) {
  //       newExpandedItems.delete(index)
  //     } else {
  //       newExpandedItems.add(index)
  //     }
  //     setExpandedItems(newExpandedItems)
  //   }

  //   const FAQs = [
  //     {
  //       question: "What does it mean when my MRI shows a herniated disc?",
  //       answer:
  //         "A herniated disc means some of the soft, gel-like material inside a spinal disc has pushed through its outer layer. This is very common and doesn't always cause symptoms. When it does, pain or numbness typically improves over weeks to months with appropriate treatment. Your referring physician can recommend the right next steps based on your specific findings.",
  //     },
  //     {
  //       question: "Is a second opinion on my MRI a good idea?",
  //       answer:
  //         "A second opinion is always reasonable — especially for complex findings or when you're considering surgery. Expert Radiology offers a formal second opinion service where a board-certified subspecialist reviews your images and provides an independent written report, typically within 24–48 hours.",
  //     },
  //     {
  //       question: "What is sciatica and how is it treated?",
  //       answer:
  //         "Sciatica is pain that radiates along the sciatic nerve — from the lower back, through the hip, and down the leg. It's often caused by a herniated disc or bone spur pressing on the nerve. Most cases resolve with physical therapy, anti-inflammatory medications, and time. Surgery is considered only when conservative treatments haven't worked after several weeks.",
  //     },
  //     {
  //       question: "Who reads my MRI report?",
  //       answer:
  //         "Your report was interpreted by a board-certified radiologist at Expert Radiology — a subspecialty-trained physician who focuses exclusively on reading and interpreting medical imaging. All reports are reviewed under our quality framework before delivery.",
  //     },
  //     {
  //       question: "How do I speak with someone about my results?",
  //       answer:
  //         "Please contact the ordering physician or facility that sent your MRI for a full clinical consultation. They have access to your complete report and are best positioned to explain how these findings relate to your symptoms and treatment options.",
  //     },
  //   ]

  return (
    <div className="-my-2">
      {isPreview && (
        <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 text-center">
          <p className="text-xs text-amber-900">
            <span className="font-bold uppercase tracking-wider">Preview</span>
            <span className="mx-2 text-amber-500">·</span>
            Internal view of the patient page. Nothing is tracked and feedback
            cannot be submitted.
          </p>
        </div>
      )}

      <header className="bg-primary text-white text-center py-5">
        <div className="border border-gray-600 px-3 py-1 inline-block rounded-full uppercase text-[10px] md:text-xs text-[#60A5FA] font-medium tracking-wide backdrop-blur-sm bg-white/10">
          Your Personalized Report
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold tracking-wide mb-4">
          <span className="text-white">Understanding Your</span>
          <br />
          <span className="text-[#60A5FA]">MRI Findings</span>
        </h1>
        <p className="text-[11px] md:text-sm leading-relaxed text-gray-400 font-medium">
          Your radiologist identified findings worth explaining in plain
          language.
          <br />
          These short videos will help you understand what was seen and what it
          <br />
          means for you.
        </p>
      </header>

      {(!uuid ||
        (!educationalVideoData &&
          !isEducationalVideoDataLoading &&
          !error)) && (
        <div className="flex items-center justify-center min-h-20">
          <p className="text-gray-600/80 italic text-base">
            No video available or invalid ID. If you believe this is an error,
            please contact support.
          </p>
        </div>
      )}

      {isEducationalVideoDataLoading && (
        <div className="flex items-center justify-center min-h-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading educational videos...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center min-h-40 px-4 mt-8">
          <p className="text-gray-500 italic mb-6 leading-relaxed text-center">
            We could not find report videos. We are looking into this issue.
            <br />
            please contact support at{" "}
            <a
              className="text-blue-500 hover:text-blue-700 hover:underline"
              href="mailto:support@expertradiology.com"
            >
              support@expertradiology.com
            </a>
          </p>
        </div>
      )}

      {educationalVideoData && (
        <div className="py-12 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600 mb-4">
              educational content
            </h6>
            <h2 className="text-3xl font-extrabold tracking-wide text-primary mb-4">
              Your Personalized Videos
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Watch these short videos to understand some of your specific
              findings and what they might mean for your health.
            </p>
          </div>

          <div className="flex items-start gap-3 bg-blue-50/60 border border-blue-100 rounded-xl px-5 py-4 max-w-3xl mx-auto mb-8">
            <FontAwesomeIcon
              className="w-4 h-4 text-blue-600 shrink-0 mt-1.5"
              icon={faCircleInfo}
            />
            <p className="text-gray-500 leading-relaxed text-left font-semibold">
              The information provided below is for educational purposes only
              and is not intended to provide treatment guidelines, medical
              advice, or treatment recommendations. Any decisions regarding your
              care should be made in consultation with your treating physician.
            </p>
          </div>

          <div className="grid gap-8 md:gap-12">
            {educationalVideoData.videos.map((video, index) => (
              <div
                key={video.VideoId ?? index}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="aspect-video w-full">
                  <VideoPlayer
                    enabled={!isPreview}
                    source={source}
                    title={video.VideoTitle || `Educational Video ${index + 1}`}
                    trackingIds={trackingIds}
                    uuid={uuid}
                    video={video}
                  />
                </div>
                {video.VideoTitle && (
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {video.VideoTitle}
                    </h3>
                    {video.description && (
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {video.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Only offered once the report resolves — a response submitted against an
          invalid UUID could never be attributed to a report or a facility. */}
      {educationalVideoData && (
        <>
          {/* Dropped once submitted — the invitation to answer is meaningless
              when the form is already gone. */}
          {!isSubmitted && (
            <div className="flex flex-col gap-4 py-16 px-4 max-w-5xl mx-auto text-center">
              <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
                quick feedback
              </h6>
              <h2 className="text-3xl font-extrabold tracking-wide text-primary">
                How was your experience?
              </h2>
              <h6 className="text-sm text-gray-500 leading-relaxed">
                Takes 30 seconds - completely anonymous.
              </h6>
            </div>
          )}

          {!isSubmitted ? (
            <Formik
              initialValues={{
                comments: "",
                illustrations_helpful: undefined,
                recommend_report: undefined,
                report_delivery: "",
                spe_helpful: undefined,
              }}
              validationSchema={feedbackSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                // Belt and braces: the button is disabled in preview, but this
                // makes it impossible for an internal view to create a real
                // feedback response by any route.
                if (isPreview || isFeedbackSubmitting) return
                try {
                  await submitFeedback(
                    {
                      Comments: values.comments,
                      IllustrationsHelpful: values.illustrations_helpful,
                      RecommendReport: values.recommend_report,
                      ReportDelivery: values.report_delivery,
                      SpeHelpful: values.spe_helpful,
                      // The QR code this response came from. The API resolves the
                      // accession, message and inbound message from it, and logs
                      // the matching feedback_submitted scan event.
                      ReportVideosUuid: uuid,
                      AccessionNo: educationalVideoData?.AccessionNo,
                      MessageId: educationalVideoData?.MessageId,
                      // Named to match this endpoint's PascalCase payload — it
                      // strips unknown keys, so a spread of the camelCase
                      // tracking ids would be silently discarded.
                      VisitorId: trackingIds.visitorId,
                      SessionId: trackingIds.sessionId,
                    },
                    {
                      onSuccess: () => {
                        setSubmitting(false)
                        resetForm()
                        setIsSubmitted(true)
                      },
                      onError: (error) => {
                        setSubmitting(false)
                        console.error("Failed to submit feedback:", error)
                      },
                    }
                  )
                } catch (error) {
                  setSubmitting(false)
                  console.error("Failed to submit feedback:", error)
                }
              }}
            >
              {({ values, setFieldValue, isValid, dirty }) => {
                const isSubmitDisabled =
                  isPreview || isFeedbackSubmitting || !isValid || !dirty

                return (
                  <Form
                    className="bg-white rounded-2xl border border-gray-200 shadow-md max-w-2xl mx-auto mb-10 overflow-hidden"
                    onClickCapture={handleFormInteraction}
                    onFocusCapture={handleFormInteraction}
                  >
                    {/* Questions section */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">
                        Rate Your Experience
                      </p>
                    </div>
                    <div className="px-6 py-6 space-y-5">
                      {/* Did the medical illustrations help? */}
                      <div className="space-y-2.5">
                        <p className="text-sm font-medium text-gray-700">
                          Did the medical illustrations help you understand your
                          diagnosis?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                            { label: "N/A", value: null },
                          ].map(({ label, value }) => (
                            <button
                              key={label}
                              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                                values.illustrations_helpful === value
                                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                              }`}
                              type="button"
                              onClick={() =>
                                setFieldValue("illustrations_helpful", value)
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <ErrorMessage
                          className="text-red-500 text-xs"
                          component="div"
                          name="illustrations_helpful"
                        />
                      </div>

                      <div className="h-px bg-gray-100" />

                      {/* Would you recommend? */}
                      <div className="space-y-2.5">
                        <p className="text-sm font-medium text-gray-700">
                          Would you recommend this type of report to others?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ].map(({ label, value }) => (
                            <button
                              key={label}
                              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                                values.recommend_report === value
                                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                              }`}
                              type="button"
                              onClick={() =>
                                setFieldValue("recommend_report", value)
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <ErrorMessage
                          className="text-red-500 text-xs"
                          component="div"
                          name="recommend_report"
                        />
                      </div>

                      <div className="h-px bg-gray-100" />

                      {/* How did you receive your report? */}
                      <div className="space-y-2.5">
                        <p className="text-sm font-medium text-gray-700">
                          How did you receive your report?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            "Doctor's office",
                            "Email",
                            "Patient portal",
                            "Other",
                          ].map((opt) => (
                            <button
                              key={opt}
                              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                                values.report_delivery === opt
                                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                              }`}
                              type="button"
                              onClick={() =>
                                setFieldValue("report_delivery", opt)
                              }
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                        <ErrorMessage
                          className="text-red-500 text-xs"
                          component="div"
                          name="report_delivery"
                        />
                      </div>

                      <div className="h-px bg-gray-100" />

                      {/* Was the SPE helpful? */}
                      <div className="space-y-2.5">
                        <p className="text-sm font-medium text-gray-700">
                          Was the Simplified Patient Explanation helpful?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                            { label: "Didn't read it", value: null },
                          ].map(({ label, value }) => (
                            <button
                              key={label}
                              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                                values.spe_helpful === value
                                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                              }`}
                              type="button"
                              onClick={() =>
                                setFieldValue("spe_helpful", value)
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <ErrorMessage
                          className="text-red-500 text-xs"
                          component="div"
                          name="spe_helpful"
                        />
                      </div>

                      <div className="h-px bg-gray-100" />

                      {/* Comments */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Additional comments{" "}
                          <span className="font-normal text-gray-400">
                            (optional)
                          </span>
                        </label>
                        <Field
                          as="textarea"
                          className="w-full p-3 border border-gray-200 rounded-xl resize-none h-24 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          maxLength={500}
                          name="comments"
                          placeholder="Anything you'd like us to know..."
                        />
                        <div className="flex items-start justify-between">
                          <ErrorMessage
                            className="text-red-500 text-xs"
                            component="div"
                            name="comments"
                          />
                          <span className="text-xs text-gray-400 ml-auto">
                            {values.comments.length}/500
                          </span>
                        </div>
                      </div>

                      <p className="text-xs text-gray-400">
                        Your feedback is anonymous. We do not collect your name,
                        email, or any identifying information.
                      </p>

                      <button
                        className={`w-full py-3 px-4 rounded-xl font-semibold text-sm text-white transition-all ${
                          // Derived from the same flag as `disabled` below, so the
                          // button can never look clickable while it isn't. Mid-submit
                          // keeps a muted blue rather than grey, so the white spinner
                          // stays legible.
                          !isSubmitDisabled
                            ? "bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md"
                            : isFeedbackSubmitting
                              ? "bg-blue-400 cursor-not-allowed"
                              : "bg-gray-300 cursor-not-allowed"
                        }`}
                        disabled={isSubmitDisabled}
                        title={
                          isPreview
                            ? "Feedback cannot be submitted from a preview"
                            : undefined
                        }
                        type="submit"
                      >
                        {isPreview ? (
                          "Submit Feedback (disabled in preview)"
                        ) : isFeedbackSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <FontAwesomeIcon
                              className="w-4 h-4 animate-spin"
                              icon={faSpinner}
                            />
                            Submitting...
                          </span>
                        ) : (
                          "Submit Feedback"
                        )}
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          ) : (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-300 max-w-3xl mx-auto mt-16 mb-8">
              <div className="flex flex-col items-center justify-center space-y-4 py-8">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon
                    className="w-10 h-10 text-white"
                    icon={faCheck}
                  />
                </div>
                <h3 className="text-xl font-bold text-primary">
                  Thanks for your feedback!
                </h3>
                <p className="text-sm text-gray-600/80 text-center max-w-md leading-relaxed">
                  Your response was submitted anonymously — we did not collect
                  your name, email, or any identifying information.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* <div className="flex flex-col gap-4 py-16 px-4 max-w-2xl mx-auto text-center">
        <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
          common questions
        </h6>
        <h2 className="text-3xl font-extrabold tracking-wide text-primary">
          Frequently Asked Questions
        </h2>
        <h6 className="text-sm text-gray-500 leading-relaxed">
          Quick answers about your findings and next steps.
        </h6>
        {FAQs.map((faq, index) => {
          const isExpanded = expandedItems.has(index)
          return (
            <div
              key={index}
              className="text-left bg-gray-100 rounded-xl shadow-sm border border-gray-300"
            >
              <div
                className="flex justify-between border-b border-blue-100 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpanded(index)}
              >
                <h3 className="text-sm md:text-base font-semibold text-primary">
                  {faq.question}
                </h3>
                <FontAwesomeIcon
                  className={`w-2 h-2 cursor-pointer rounded-full p-2 transition-all duration-300 ${
                    isExpanded
                      ? "bg-blue-600 text-gray-50 transform rotate-45"
                      : "bg-blue-200 text-primary hover:text-primary/80"
                  }`}
                  icon={faPlus}
                />
              </div>
              {isExpanded && (
                <p className="text-gray-700/80 text-xs md:text-sm p-4 leading-relaxed whitespace-pre-wrap animate-fadeIn">
                  {faq.answer}
                </p>
              )}
            </div>
          )
        })}
      </div> */}

      <div className="bg-primary py-12 px-2 mb-8 text-center text-white rounded-3xl max-w-5xl mx-auto">
        <h2 className="text-xl md:text-3xl font-bold mb-4">
          Want a Specialist&apos;s Second Opinion?
        </h2>
        <p className="text-xs text-gray-300 mb-8 max-w-xl mx-auto">
          Upload your images and get a written report from a
          subspecialty-trained radiologist — delivered in 24–48 hours.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 font-medium"
            href="https://www.expertradiology.com/solutions/attorneys/"
          >
            <span className="material-icons text-xs">check_circle</span>
            Get a Second Opinion
          </a>
          <a
            className="border border-gray-600 hover:border-gray-400 text-white px-6 py-3 rounded-xl flex items-center gap-3 transition-colors"
            href="https://www.expertradiology.com/solutions/clinicians"
          >
            <span className="text-gray-400 text-xs font-medium italic font-serif">
              PrecisionPlus v3™
            </span>
            <span className="text-gray-300 font-medium">Learn more →</span>
          </a>
        </div>
      </div>

      <footer className="bg-primary text-white text-center py-8 px-4">
        <div className="flex items-center justify-center gap-4 mb-6">
          <img alt="Expert Radiology" className="h-8" src="/logo.png" />
          <div className="w-px h-6 bg-gray-600"></div>
          <span className="text-gray-400 text-xs font-bold italic font-serif">
            PrecisionPlus v3™
          </span>
        </div>
        <p className="text-gray-500 text-xs mb-4 max-w-xl mx-auto leading-relaxed">
          These videos are for educational purposes only and do not constitute
          medical advice. Always consult your treating physician regarding your
          specific condition and treatment options.
        </p>
        <p className="text-xs mb-4">
          <a
            className="text-blue-500 hover:text-blue-300"
            href="https://www.expertradiology.com"
          >
            expertradiology.com
          </a>
          <span className="text-gray-400 mx-2">·</span>
          <a
            className="text-blue-500 hover:text-blue-300"
            href="https://www.expertradiology.com/solutions/attorneys/"
          >
            Second Opinions
          </a>
          <span className="text-gray-400 mx-2">·</span>
          <a
            className="text-blue-500 hover:text-blue-300"
            href="tel:4159002000"
          >
            415-900-2000
          </a>
        </p>
        <p className="text-[10px] text-gray-400/40">
          © {new Date().getFullYear()} Expert Radiology Management Services,
          LLC. All rights reserved.
          <span className="mx-2">·</span>
          HIPAA Compliant
        </p>
      </footer>
    </div>
  )
}

export default EducationalVideos
